// Traduce el campo que rechazó el servidor (`data.field` del 422) a un mensaje legible.
// Nunca muestra la ruta del DTO: un campo sin etiqueta devuelve `null` y el caller usa su genérico.
import { ApiError } from '@/api/errors';
import { FORM_MESSAGES } from './salePromotionForm';

export const CODE_TEXT_RULE = 'El código lleva de 1 a 32 letras, números, guion o guion bajo.';
export const PAST_VALID_UNTIL = 'El fin de la vigencia ya pasó. Elige hoy o una fecha posterior.';

export const SALE_PROMOTION_FIELD_LABELS: Readonly<Record<string, string>> = {
    budgetMaxCents: 'Presupuesto tope',
    customerText: 'Descripción',
    'levers.accessoryDiscount.amountCents': 'Monto del descuento en accesorios',
    'levers.accessoryDiscount.maxAmountCents': 'Tope del descuento en accesorios',
    'levers.accessoryDiscount.percentBps': 'Porcentaje del descuento en accesorios',
    'levers.airtime.rechargeAmountCents': 'Monto de cada recarga',
    'levers.airtime.rechargeCount': 'Número de recargas',
    'levers.coupon.amountCents': 'Monto del cupón',
    'levers.coupon.percentBps': 'Porcentaje del cupón',
    'levers.coupon.validityDays': 'Días de vigencia del cupón',
    'levers.deviceDiscount.amountCents': 'Monto del descuento del equipo',
    'levers.deviceDiscount.maxAmountCents': 'Tope del descuento del equipo',
    'levers.deviceDiscount.percentBps': 'Porcentaje del descuento del equipo',
    'levers.gift.accessorySubcategory': 'Subcategoría de accesorios',
    'levers.gift.chooseCount': 'Cuántos elige el cliente',
    'levers.gift.maxPriceCents': 'Precio máximo del regalo',
    'levers.gift.skus': 'Accesorios de regalo',
    'levers.walletBonus.amountCents': 'Monto del bono al Wallet',
    levers: 'Palancas',
    name: 'Nombre',
    oncePerCustomer: 'Una vez por cliente',
    'scope.productCategories': 'Tipo de producto del alcance',
    'scope.skus': 'Productos del alcance',
    'scope.storeIds': 'Tiendas del alcance',
    'scope.tiers': 'Gama del alcance',
    'segmentation.requiresActiveSurekeepCredit': 'Crédito SureKeep activo',
    'segmentation.segmentIds': 'Segmentos',
    validFrom: 'Inicio de la vigencia',
    validUntil: 'Fin de la vigencia',
    paymentOptions: 'Forma de pago',
};

export const DISCOUNT_CODE_FIELD_LABELS: Readonly<Record<string, string>> = {
    appliesTo: 'Con qué financiera aplica',
    codeText: 'Código',
    'discount.amountCents': 'Monto del descuento',
    'discount.percentBps': 'Porcentaje del descuento',
    maxUses: 'Usos totales',
    skus: 'Equipos',
    storeIds: 'Tiendas',
    validFrom: 'Desde',
    validUntil: 'Hasta',
};

// Campos cuya regla no depende del mensaje del servidor.
const FIXED_MESSAGES: Readonly<Record<string, string>> = {
    codeText: CODE_TEXT_RULE,
    'levers.gift.accessorySubcategory': FORM_MESSAGES.giftSubcategory,
    'levers.gift.skus': FORM_MESSAGES.giftSkus,
    levers: FORM_MESSAGES.levers,
    name: FORM_MESSAGES.name,
};

const MONEY_LEAVES = new Set([
    'amountCents',
    'budgetMaxCents',
    'maxAmountCents',
    'maxPriceCents',
    'rechargeAmountCents',
]);
const COUNT_LEAVES = new Set([
    'chooseCount',
    'extraMonths',
    'maxUses',
    'rechargeCount',
    'validityDays',
]);

function fieldOf(error: ApiError): string | null {
    const data = error.data;
    if (typeof data !== 'object' || data === null) return null;
    const field = (data as { field?: unknown }).field;
    return typeof field === 'string' && field !== '' ? field : null;
}

/** El tope del rango «entre 1 y N» que manda el servidor, si viene. */
function upperBound(message: string): string | null {
    return /entre \d+ y (\d+)/.exec(message)?.[1] ?? null;
}

const lower = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

function ruleFor(field: string, label: string, serverMessage: string): string {
    const fixed = FIXED_MESSAGES[field];
    if (fixed) return fixed;
    const leaf = field.split('.').pop() ?? field;
    const max = upperBound(serverMessage);
    if (leaf === 'chooseCount' && max !== null)
        return `Cuántos elige el cliente no puede ser mayor que los regalos de la lista (${max}).`;
    if (MONEY_LEAVES.has(leaf)) return `«${label}»: ${lower(FORM_MESSAGES.money)}`;
    if (leaf === 'percentBps') return `«${label}»: ${lower(FORM_MESSAGES.percent)}`;
    if (COUNT_LEAVES.has(leaf))
        return max === null
            ? `«${label}»: ${lower(FORM_MESSAGES.count)}`
            : `«${label}»: pon un número entero entre 1 y ${max}.`;
    if (leaf === 'validFrom' || leaf === 'validUntil') {
        if (/posterior/.test(serverMessage)) return FORM_MESSAGES.range;
        if (/ya pasó/.test(serverMessage)) return PAST_VALID_UNTIL;
        return `«${label}»: elige una fecha válida.`;
    }
    return `Revisa «${label}» y guarda de nuevo.`;
}

/** Mensaje legible del campo rechazado; `null` si el error no trae un campo conocido. */
export function serverFieldMessage(
    error: unknown,
    labels: Readonly<Record<string, string>>,
): string | null {
    if (!(error instanceof ApiError)) return null;
    const field = fieldOf(error);
    if (field === null || !Object.hasOwn(labels, field)) return null;
    return ruleFor(field, labels[field], error.msg);
}
